import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { DateTimeInfo } from "../../DateTimeInfo";
import { EntityCommand, EntityCommandTypeEnum } from "../../EntityCommand";
import { EntityCommandInformation } from "../../EntityCommandInformation";
import { AndExpression } from "../../Expressions/AndExpression";
import { ComparisonExpression, ComparisonExpressionOperatorEnum } from "../../Expressions/ComparisonExpression";
import { Expression } from "../../Expressions/Expression";
import { OrExpression } from "../../Expressions/OrExpression";
import { Constructor, Nullable, PartialNullable } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { LinqArray } from "../../LinqArray";
import { PermissionCacheService } from "../../PermissionCacheService";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyObject, PropertyString } from "../../RadObject";
import { WindowShim } from "../../RadWindow";
import { AjaxError } from "../../Server/AjaxError";
import { PermissionEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PermissionEvaluator";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { DetailsPage } from "../Pages/DetailsPage";
import { Button } from "./Button";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { EntitySearchSortDirectionEnum } from "./EntitySearchSortDirectionEnum";
import { ErrorDialog } from "./ErrorDialog";
import { InputControl } from "./InputControl";
import { Menu } from "./Menu";
import { Panel } from "./Panel";
import { Table, TableRow, TableRowSelectTypeEnum } from "./Table";
import { TableCellEntitySearch } from "./TableCellEntitySearch";
import { ITableColumn } from "./TableColumn";
import { TableColumnEntityAction } from "./TableColumnEntityAction";
import { TableColumnEntitySaveIndicator } from "./TableColumnEntitySaveIndicator";
import { TableColumnEntitySearch } from "./TableColumnEntitySearch";
import { TableRowEntitySearch } from "./TableRowEntitySearch";
import { Text } from "./Text";
import { TextBox } from "./TextBox";
import { NAMESPACE } from "./_Namespace";

import "./EntitySearch.scss";

const
    radWindow = WindowShim();

// #region EntitySearch

interface CellChange { listProperty: string, change: { path: string, tableRow: TableRowEntitySearch } };

export interface IEntitySearchDataProperty
{
    applicationEntitySupportsSoftDelete?: boolean;
    defaultSort?: boolean;
    formatString?: string;
    isString?: boolean;
    manual?: boolean;
    name?: string;
    path?: string;
    sortable?: boolean;
    tableColumnClass?: Constructor<ITableColumn>;
    viewControl?: string;
    viewControlClass?: Constructor<IControl>
}

export type EntitySearchPropertyValues =
    ControlPropertyValues
    & {
        applicationEntityName: string;
        applicationEntityNameFriendly: string;
        applicationEntityNamePlural: string;
        applicationEntityNamePluralFriendly: string;
        applicationEntitySupportsRowVersion: boolean;
        applicationEntitySupportsSoftDelete: boolean;
        baseFilter: any;
        dataServiceId: string;
        dataServiceData: string;
        dataServiceOperation: string;
        defaultSortListProperty: string;
        defaultSortListPropertyDirection: string;
        editMode: boolean;
        entityIconDataSourcePath: string;
        foreignApplicationEntityAlias: string;
        includeSoftDeleted: boolean;
        hideSaveIndicator: boolean;
        lightweightTable: boolean;
        listProperties: IEntitySearchDataProperty[];
        messageWhenNoResults: string;
        openRecordInDialog: boolean;
        pageSize: number;
        primaryApplicationEntityName: string;
        primaryApplicationEntityNameFriendly: string;
        primaryApplicationEntityNamePlural: string;
        primaryApplicationEntityNamePluralFriendly: string;
        requiredProperties: IEntitySearchDataProperty[];
        rowSelectType: TableRowSelectTypeEnum;
        searchResultsCount: number;
        searchResultsResponse: any;
        sortable: boolean;
        showEntityCommands: boolean;
        showEntityIcons: boolean;
        showEntitySetCommands: boolean;
        showQuickSearch: boolean;
        sortByOrderProperty: boolean;
        tableColumnClass: any;
        tableColumnEntityActionClass: any;
        tableRowClass: any;
    };

export type EntitySearchProperties = {
    [name in keyof EntitySearchPropertyValues]: Property<EntitySearchPropertyValues[name]>
};

export interface IEntitySearch extends IControl, EntitySearchProperties
{
    _getEntityCommandInformation(): EntityCommandInformation;
    _hasEntityCommandsForEntityCommandInformation(entityCommandInformation: Nullable<EntityCommandInformation>): boolean;
    _setSortListProperty(propertyPath?: string): void
    _updateTableColumnEntityAction(): void

    openEntityCommandMenuAsync(entityCommandInformation: EntityCommandInformation): Promise<void>;
    openEntitySetCommandMenuAsync(entityCommandInformation: EntityCommandInformation): Promise<void>;
    propertiesGet(): string[];
    queueSearchAsync(page?: number, filter?: any, force?: boolean): void;
    rowMoveDown(row: TableRowEntitySearch): void;
    rowMoveUp(row: TableRowEntitySearch): void;
    saveManualCellChangeAsync(listProperty: any, change: { path: string, tableRow: TableRowEntitySearch }): Promise<void>;
    saveManualCellChangesAsync(row: TableRowEntitySearch, changes: CellChange[]): Promise<void>;
    saveRowAsync(row: TableRowEntitySearch): Promise<void>;
    searchAsync(page?: number, filter?: any, force?: boolean): Promise<void>;
    setupListPropertiesAsync(): Promise<void>;
    updateRowAsync(id: string): Promise<void>
}

export class EntitySearch extends Control implements IEntitySearch
{
    // #region Static

    public static override classNamespace = NAMESPACE + "EntitySearch";
    public static override className = "EntitySearch";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<EntitySearchPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                applicationEntitySupportsRowVersion: true,
                applicationEntitySupportsSoftDelete: false,
                dataSourceControlSelector: ".rad-control-page",
                defaultSortListPropertyDirection: EntitySearchSortDirectionEnum.ascending,
                editMode: false,
                hideSaveIndicator: false,
                includeSoftDeleted: false,
                lightweightTable: false,
                messageWhenNoResults: "No records found",
                pageSize: 10,
                rowSelectType: TableRowSelectTypeEnum.none,
                showEntityCommands: true,
                showEntityIcons: true,
                showEntitySetCommands: true,
                showQuickSearch: true,
                sortable: true,
                sortByOrderProperty: false,
                tableColumnClass: TableColumnEntitySearch,
                tableColumnEntityActionClass: TableColumnEntityAction,
                tableRowClass: TableRowEntitySearch,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected __settingUpListProperties?: boolean;
    protected _queueSearchAsyncTimeoutId?: number;
    protected _setupListProperties?: boolean;
    protected _textBoxSearchValueChangedTimeoutId?: number;
    protected buttonEntitySetControlOpenActionMenu?: Button;
    protected buttonFirst?: Button;
    protected buttonLast?: Button;
    protected buttonNext?: Button;
    protected buttonPrevious?: Button;
    protected buttonRefresh?: Button;
    protected currentFilter?: any;
    protected currentPage: number;
    protected designModePanel?: Panel;
    protected entityCommandMenu?: Nullable<Menu>;
    protected entityCommands?: EntityCommand[];
    protected entitySetCommandMenu?: Menu;
    public entitySetCommands?: EntityCommand[];
    protected loaded: boolean;
    protected paginationContainer?: Panel;
    protected panelEntitySetControls?: Panel;
    protected panelSpacer?: Panel;
    public tableSearchResults?: Table;
    protected textBoxSearch?: TextBox;
    protected textPaginationStatus?: Text;
    protected totalRecords: Nullable<number>;

    // #endregion

    // #region Properties

    public applicationEntityName!: PropertyString;
    public applicationEntityNameFriendly!: PropertyString;
    public applicationEntityNamePlural!: PropertyString;
    public applicationEntityNamePluralFriendly!: PropertyString;
    public applicationEntitySupportsRowVersion!: PropertyBoolean;
    public applicationEntitySupportsSoftDelete!: PropertyBoolean;
    public baseFilter!: PropertyObject;
    public dataServiceId!: PropertyString;
    public dataServiceData!: PropertyString;
    public dataServiceOperation!: PropertyString;
    public defaultSortListProperty!: PropertyString;
    public defaultSortListPropertyDirection!: PropertyString;
    public editMode!: PropertyBoolean;
    public entityIconDataSourcePath!: PropertyString;
    public foreignApplicationEntityAlias!: PropertyString;
    public includeSoftDeleted!: PropertyBoolean;
    public hideSaveIndicator!: PropertyBoolean;
    public lastSearch!: PropertyString;
    public lightweightTable!: PropertyBoolean;
    public listProperties!: Property<IEntitySearchDataProperty[]>;
    public messageWhenNoResults!: PropertyString;
    public openRecordInDialog!: PropertyBoolean;
    public pageSize!: PropertyInt32;
    public primaryApplicationEntityName!: PropertyString;
    public primaryApplicationEntityNameFriendly!: PropertyString;
    public primaryApplicationEntityNamePlural!: PropertyString;
    public primaryApplicationEntityNamePluralFriendly!: PropertyString;
    public requiredProperties!: Property<IEntitySearchDataProperty[]>;
    public rowSelectType!: Property<TableRowSelectTypeEnum>;
    public searchResultsCount!: PropertyInt32;
    public searchResultsResponse!: PropertyObject;
    public sortable!: PropertyBoolean;
    public showEntityCommands!: PropertyBoolean;
    public showEntityIcons!: PropertyBoolean;
    public showEntitySetCommands!: PropertyBoolean;
    public showQuickSearch!: PropertyBoolean;
    public sortByOrderProperty!: PropertyBoolean;
    public tableColumnClass!: PropertyObject;
    public tableColumnEntityActionClass!: PropertyObject;
    public tableRowClass!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<EntitySearchPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-entity-search");

        Assert.isNotNullOrUndefined(this.tableSearchResults, "this.tableSearchResults");
        Assert.isNotNullOrUndefined(this.tableSearchResults.tableHead, "this.tableSearchResults.tableHead");

        this.tableSearchResults.addCssClass("rad-search-results");
        this.tableSearchResults.tableHead.addCssClass("rad-device-visible-when-narrow");

        this.loaded = false;
        this.currentPage = 0;
        this.totalRecords = null;

        this.currentFilter = null;

        this._textBoxSearchValueChangedTimeoutId = 0;

        // Trigger Property Changes
        this.applicationEntityName.triggerChange();
        this.applicationEntityNameFriendly.triggerChange();
        this.applicationEntityNamePlural.triggerChange();
        this.applicationEntityNamePluralFriendly.triggerChange();
        this.applicationEntitySupportsSoftDelete.triggerChange();
        this.defaultSortListProperty.triggerChange();
        this.defaultSortListPropertyDirection.triggerChange();
        this.editMode.triggerChange();
        this.includeSoftDeleted.triggerChange();
        this.lightweightTable.triggerChange();
        this.listProperties.triggerChange();
        this.pageSize.triggerChange();
        this.rowSelectType.triggerChange();
        this.showQuickSearch.triggerChange();
        this.sortByOrderProperty.triggerChange();
        this.tableRowClass.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public openEntityCommandMenuAsync(entityCommandInformation: EntityCommandInformation): Promise<void>
    {
        if (this.entityCommandMenu)
        {
            this.entityCommandMenu.controlsGet()
                .forEach(item =>
                {
                    const
                        visibleEvaluator = item.visibleEvaluator.get();

                    if (visibleEvaluator)
                        visibleEvaluator.state.set(entityCommandInformation);
                });

            return this._openCommandMenuAsync(this.entityCommandMenu, entityCommandInformation);
        }

        return Promise.resolve();
    }
    public openEntitySetCommandMenuAsync(entityCommandInformation: EntityCommandInformation): Promise<void>
    {
        Assert.isNotNullOrUndefined(this.entitySetCommandMenu, "this.entitySetCommandMenu");

        this.entitySetCommandMenu.controlsGet()
            .forEach(item =>
            {
                const
                    visibleEvaluator = item.visibleEvaluator.get();

                if (visibleEvaluator)
                    visibleEvaluator.state.set(entityCommandInformation);
            });

        return this._openCommandMenuAsync(this.entitySetCommandMenu, entityCommandInformation);
    }
    public propertiesGet(): string[]
    {
        const
            properties = [];

        Assert.isNotNullOrUndefined(this.tableSearchResults, "this.tableSearchResults");

        this.tableSearchResults.columnsGet()
            .filter((item): item is TableColumnEntitySearch => item instanceof TableColumnEntitySearch)
            .filter(item => !item.manual.get())
            .forEach(item =>
            {
                const
                    dataSourceProperty = item.dataSourceProperty.get();

                if (dataSourceProperty)
                {
                    properties.push(dataSourceProperty);

                    if (item.applicationEntitySupportsSoftDelete.get())
                    {
                        const
                            pathParts = dataSourceProperty.split(".");

                        if (pathParts.length > 1)
                        {
                            pathParts.pop();
                            properties.push(StringHelper.format("{0}.deletedGuid", pathParts.join(".")));
                        }
                    }
                }

            });

        if (this.applicationEntitySupportsSoftDelete.get())
            properties.push("deletedGuid");

        return properties;
    }
    public queueSearchAsync(page?: number, filter?: any, force?: boolean): void
    {
        return this._queueSearchAsync(page, filter, force);
    }
    public rowMoveDown(row: TableRowEntitySearch): void
    {
        if (!this.sortByOrderProperty.get())
            return; // Not supported

        this._changeRowOrderAndRefresh(row, false);
    }
    public rowMoveUp(row: TableRowEntitySearch): void
    {
        if (!this.sortByOrderProperty.get())
            return; // Not supported

        this._changeRowOrderAndRefresh(row, true);
    }
    public saveManualCellChangeAsync(listProperty: any, change: { path: string, tableRow: TableRowEntitySearch }): Promise<void>
    {
        const
            row = change.tableRow,
            tableCell = ArrayHelper.firstOrDefault(change.tableRow.controlsGet(), item => (item as any).dataSourceProperty.get && (item as any).dataSourceProperty.get() === change.path) as Nullable<TableCellEntitySearch>,
            $$tableCell = tableCell ? tableCell.$$element.get() : null;

        if (row.hasCssClass("rad-state-saving"))
            return Promise.reject("Already saving");

        if (!tableCell || tableCell.hasCssClass("rad-state-saving"))
            return Promise.reject("Already saving");

        row.removeCssClass("rad-state-error");
        row.removeCssClass("rad-state-saved");
        tableCell.removeCssClass("rad-state-error");
        tableCell.removeCssClass("rad-state-saved");

        row.addCssClass("rad-state-saving");
        tableCell.addCssClass("rad-state-saving");

        this._validationErrorsHide(row, tableCell);
        this._updatePagination();

        this._rowDisable(row);

        Assert.isNotNullOrUndefined($$tableCell, "$$tableCell");

        $$tableCell.attr("tabindex", "0");
        tableCell.focus();

        return (
            this._saveManualCellChangeAsync(listProperty, change)
                .then(() =>
                {
                    row.addCssClass("rad-state-saved");
                    tableCell.addCssClass("rad-state-saved");
                })
                .catch(error =>
                {
                    row.addCssClass("rad-state-error");
                    tableCell.addCssClass("rad-state-error");

                    if (error?.validationErrors)
                        this._validationErrorsShow(row, tableCell, error.validationErrors);

                    throw error;
                })
                .finally(() =>
                {
                    row.removeCssClass("rad-state-saving");
                    tableCell.removeCssClass("rad-state-saving");

                    this._rowEnable(row);

                    if ($$(":focus").is($$tableCell))
                        tableCell._editorControl?.focusNextControl();

                    $$tableCell.removeAttr("tabindex");

                    this._updatePagination();
                })
        );
    }
    public saveManualCellChangesAsync(row: TableRowEntitySearch, changes: CellChange[]): Promise<void>
    {
        if (row.hasCssClass("rad-state-saving"))
            return Promise.reject("Already saving");

        row.removeCssClass("rad-state-error");
        row.removeCssClass("rad-state-saved");

        row.addCssClass("rad-state-saving");

        this._validationErrorsHide(row, null);
        this._updatePagination();

        this._rowDisable(row);

        return (
            Promise
                .all(
                    changes
                        .map(item => this._saveManualCellChangeAsync(item.listProperty, item.change))
                )
                .then(() =>
                {
                    row.addCssClass("rad-state-saved");
                })
                .catch(error =>
                {
                    row.addCssClass("rad-state-error");
                    //tableCell.addCssClass("rad-state-error");

                    if (error?.validationErrors)
                        this._validationErrorsShow(row, null, error.validationErrors);

                    throw error;
                })
                .finally(() =>
                {
                    row.removeCssClass("rad-state-saving");

                    this._rowEnable(row);

                    this._updatePagination();
                })
        );
    }
    public saveRowAsync(row: TableRowEntitySearch): Promise<void>
    {
        if (row.hasCssClass("rad-state-saving"))
            return Promise.reject("Already saving");

        row.removeCssClass("rad-state-error");
        row.removeCssClass("rad-state-saved");

        row.addCssClass("rad-state-saving");

        this._validationErrorsHide(row, null);
        this._updatePagination();

        this._rowDisable(row);

        return (
            this._saveRowAsync(row)
                .then(() =>
                {
                    row.addCssClass("rad-state-saved");
                })
                .catch(error =>
                {
                    row.addCssClass("rad-state-error");

                    if (error?.validationErrors)
                        this._validationErrorsShow(row, null, error.validationErrors);

                    throw error;
                })
                .finally(() =>
                {
                    row.removeCssClass("rad-state-saving");

                    this._rowEnable(row);

                    this._updatePagination();
                })
        );
    }
    public searchAsync(page?: number, filter?: any, force?: boolean): Promise<void>
    {
        if (!force && !this._shouldSearch())
            return Promise.resolve();

        const
            initialPromise =
                this._setupListProperties
                    ? Promise.resolve()
                    : this.setupListPropertiesAsync()

        this._setupListProperties = true;

        return (
            initialPromise
                .then(() =>
                {
                    if (this.dataServiceId.get())
                        return this._searchUsingDataServiceAsync(page);

                    return this._searchAsync(page, filter);
                })
        );
    }
    public setupListPropertiesAsync(): Promise<void>
    {
        this.__settingUpListProperties = true;

        return (
            this._setupListPropertiesAsync()
                .then(() =>
                {
                    this.__settingUpListProperties = false;
                })
        );
    }
    public async updateRowAsync(id: string): Promise<void>
    {
        const
            request =
                this._createSearchWebRequest(0, new ComparisonExpression({
                    operator: ComparisonExpressionOperatorEnum.equals,
                    path: "id",
                    value: id,
                })),
            response = await request.executeAsync();

        if (!response)
            return;

        if (response?.length > 1)
            return;

        const
            rows = this.tableSearchResults?.rowsGet();

        if (rows && !(rows instanceof JNative))
        {
            rows
                .filter((item): item is TableRow => item instanceof TableRow)
                .forEach(item =>
                {
                    if (item.dataSource.get()?.propertyValueByPathGet("id") === id)
                    {
                        if (response.length === 0)
                            item.remove();
                        else if (response.length === 1)
                            item.dataSource.set(new DataSource({ data: response[0] }));
                    }
                });
        }
    }

    // #endregion

    // #region Protected Methods

    protected _applicationEntityNameChanged(change: PropertyChange<string>)
    {
        this._setupEntityCommands();
        this._setupEntitySetCommands();
    }
    protected _applicationEntityNameFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _applicationEntityNamePluralChanged(change: PropertyChange<string>)
    {
        this._setTableRowIcons();
    }
    protected _applicationEntityNamePluralFriendlyChanged(change: PropertyChange<string>)
    {
        this.buttonRefresh?.elementTitle.set(change.currentValue ? `Refresh ${change.currentValue}` : null);
        this.buttonEntitySetControlOpenActionMenu?.elementTitle.set(change.currentValue ? `Open action menu for ${change.currentValue}` : null);
    }
    protected _applicationEntitySupportsRowVersionChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _applicationEntitySupportsSoftDeleteChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _baseFilterChanged(change: PropertyChange<any>)
    {
        this.searchAsync();
    }
    protected _changeRowOrderAndRefresh(row: TableRowEntitySearch, decrement: boolean)
    {
        const
            rowDataSource = row.dataSource.get();

        if (!rowDataSource)
            return;

        const
            id = rowDataSource.propertyValueByPathGet<string>("id"),
            rowVersion = rowDataSource.propertyValueByPathGet<string>("rowVersion");

        let
            order = rowDataSource.propertyValueByPathGet<number>("order");

        if (ObjectHelper.isNullOrUndefined(order))
            order = this._getRowRecordNumber(row);

        order = order + (decrement ? -1 : 1);

        this._loadingEnqueue();

        const
            applicationEntityNamePlural = this.applicationEntityNamePlural.get();

        Assert.isNotNullOrUndefined(applicationEntityNamePlural, "applicationEntityNamePlural");

        ServiceManager.getRequired(WebApiClientService)
            .createEntitySaveRequest(
                applicationEntityNamePlural,
                {
                    id: id,
                    rowVersion: rowVersion,
                    order: order
                },
                {
                    id: id,
                    rowVersion: rowVersion
                },
                false
            )
            .executeAsync()
            .then(() =>
            {
                return this.searchAsync();
            })
            .finally(() =>
            {
                this._loadingDequeue();
            });
    }
    protected _checkPermissions()
    {
        if (ServiceManager.getOptional(PermissionCacheService)?.hasPermissionForEntity(this.applicationEntityName.get(), PermissionEvaluator.permissionLevels.update))
            this.addCssClass("rad-permission-update");
        else
            this.removeCssClass("rad-permission-update");
    }
    protected _combineFilters(filter?: Expression)
    {
        const
            expressions: Expression[] = [],
            baseFilter = this.baseFilter.get(),
            textBoxSearchValue = this.textBoxSearch?.isVisible.get() ? this.textBoxSearch.value.get() : null;

        if (baseFilter)
            expressions.push(baseFilter);

        if (filter)
            expressions.push(filter);

        if (!StringHelper.isNullOrEmpty(textBoxSearchValue))
        {
            const
                listProperties = LinqArray.create(this.listProperties.get() ?? []);

            const
                textBoxSearchValueAsFloat = parseFloat(textBoxSearchValue);

            if (listProperties.length)
            {
                const
                    textBoxSearchValueAsFloat = parseFloat(textBoxSearchValue),
                    orExpression =
                        new OrExpression({
                            expressions:
                                listProperties
                                    .where(item => !item.manual)
                                    .select(item =>
                                    {
                                        if (item.isString)
                                        {
                                            return new ComparisonExpression({
                                                path: item.path,
                                                operator: ComparisonExpressionOperatorEnum.contains,
                                                value: textBoxSearchValue.trim(),
                                            });
                                        }

                                        if (!isNaN(textBoxSearchValueAsFloat) && (item.path === "referenceNumber" || item.path?.endsWith(".referenceNumber")))
                                        {
                                            return new ComparisonExpression({
                                                path: item.path,
                                                operator: ComparisonExpressionOperatorEnum.equals,
                                                value: textBoxSearchValueAsFloat
                                            });
                                        }

                                        return null;
                                    })
                                    .where(item => item !== null)
                                    .select(item => item as Expression)
                        });

                if (orExpression.expressions.get()?.length)
                    expressions.push(orExpression);
            }
        }

        if (expressions.length)
        {
            if (expressions.length === 1)
                return expressions[0];

            return new AndExpression({
                expressions: expressions
            });
        }

        return null;
    }
    protected _createEntityCommands(): EntityCommand[] | undefined
    {
        const
            applicationEntityName = this.applicationEntityName.get();

        Assert.isNotNullOrUndefined(applicationEntityName, "applicationEntityName");

        return radWindow.entityManager?.getEntityCommands(EntityCommandTypeEnum.entity, applicationEntityName);
    }
    protected _createEntitySetCommands(): EntityCommand[] | undefined
    {
        const
            applicationEntityName = this.applicationEntityName.get();

        Assert.isNotNullOrUndefined(applicationEntityName, "applicationEntityName");

        return radWindow.entityManager?.getEntityCommands(EntityCommandTypeEnum.entitySet, applicationEntityName);
    }
    protected _createSearchWebRequest(page: number, filter?: any, overridePageSize?: number)
    {
        const
            pageSize = overridePageSize !== undefined ? overridePageSize : this.pageSize.get(),
            defaultSortListPropertyDirection = this.defaultSortListPropertyDirection.get(),
            sortByOrderProperty = this.sortByOrderProperty.get(),
            dataSource = this.dataSource.get(),
            foreignApplicationEntityAlias = this.foreignApplicationEntityAlias.get(),
            additionalHeaders: { [name: string]: string } = {},
            entityIconDataSourcePath = this.entityIconDataSourcePath.get(),
            requiredProperties = this.requiredProperties.get();

        let
            defaultSortListProperty = this.defaultSortListProperty.get(),
            primaryEntityId,
            properties = LinqArray.create(this.propertiesGet()),
            sortOrder;

        if (!defaultSortListProperty && properties.length)
            defaultSortListProperty = properties[0];

        if (sortByOrderProperty)
        {
            sortOrder = "order asc";
            if (!properties.some(item => item === "order"))
                properties.push("order");
        }
        else if (defaultSortListProperty)
            sortOrder = StringHelper.format("{0} {1}", defaultSortListProperty, defaultSortListPropertyDirection === EntitySearchSortDirectionEnum.descending ? "desc" : "asc");

        if (entityIconDataSourcePath)
            properties.push(entityIconDataSourcePath);

        properties.push("id");

        if (this.applicationEntitySupportsRowVersion.get())
            properties.push("rowVersion");

        Assert.isNotNullOrUndefined(this.tableSearchResults, "this.tableSearchResults");

        this.tableSearchResults.columnsGet()
            .filter((item): item is TableColumnEntitySearch => item instanceof TableColumnEntitySearch)
            .forEach(item =>
            {
                const
                    viewControlProperties = item.viewControlProperties.get();

                if (viewControlProperties?.dataSourceProperty)
                    properties.push(viewControlProperties.dataSourceProperty);

                const
                    editorControlProperties = item.editorControlProperties.get();

                if (editorControlProperties?.dataSourceProperty)
                    properties.push(editorControlProperties.dataSourceProperty);
            });

        if (requiredProperties)
            properties.addRange(requiredProperties.filter(item => !!item.path).map(item => item.path!));

        properties =
            properties
                .where(item => !!item)
                .distinct();

        additionalHeaders["RAD-Option-Include-Soft-Deleted"] =
            this.applicationEntitySupportsSoftDelete.get() && this.includeSoftDeleted.get() ? "true" : "false";

        if (foreignApplicationEntityAlias)
        {
            Assert.isNotNullOrUndefined(dataSource, "dataSource");

            primaryEntityId = dataSource.propertyValueByPathGet<string>("id");

            Assert.isNotNullOrUndefined(primaryEntityId, "primaryEntityId");

            const
                primaryApplicationEntityNamePlural = this.primaryApplicationEntityNamePlural.get();

            Assert.isNotNullOrUndefined(primaryApplicationEntityNamePlural, "primaryApplicationEntityNamePlural");

            return (
                ServiceManager.getRequired(WebApiClientService)
                    .createEntityListChildrenRequest(
                        primaryApplicationEntityNamePlural,
                        primaryEntityId,
                        foreignApplicationEntityAlias,
                        {
                            columns: properties,
                            filter,
                            orderBy: sortOrder,
                            skip: pageSize ? page * pageSize : 0,
                            take: pageSize,
                        },
                        additionalHeaders,
                        false
                    )
            );
        }

        const
            applicationEntityNamePlural = this.applicationEntityNamePlural.get();

        Assert.isNotNullOrUndefined(applicationEntityNamePlural, "applicationEntityNamePlural");

        return (
            ServiceManager.getRequired(WebApiClientService)
                .createEntitySearchRequest(
                    applicationEntityNamePlural,
                    {
                        columns: properties,
                        filter,
                        orderBy: sortOrder,
                        skip: pageSize ? page * pageSize : 0,
                        take: pageSize,
                    },
                    additionalHeaders,
                    false
                )
        );
    }
    protected _createSearchWebRequestDataService(page: number, overridePageSize?: number)
    {
        const
            dataServiceId = this.dataServiceId.get(),
            dataServiceData = this.dataServiceData.get(),
            dataServiceOperation = this.dataServiceOperation.get(),
            pageSize = overridePageSize !== undefined ? overridePageSize : this.pageSize.get(),
            defaultSortListProperty = this.defaultSortListProperty.get(),
            defaultSortListPropertyDirection = this.defaultSortListPropertyDirection.get(),
            data = dataServiceData ? ObjectHelper.fromJson(dataServiceData) : {};

        data.skip = pageSize ? page * pageSize : 0;
        data.top = pageSize;
        data.orderBy = defaultSortListProperty;
        data.orderByDescending = defaultSortListPropertyDirection === EntitySearchSortDirectionEnum.descending;
        data.searchText = this.textBoxSearch?.isVisible.get() ? this.textBoxSearch.value.get() : null;

        return (
            ServiceManager.getRequired(WebApiClientService)
                .createApiMethodRequest(
                    "DataService",
                    "CallDataService",
                    {
                        id: dataServiceId,
                        operation: dataServiceOperation,
                        data: ObjectHelper.toJson(data)
                    },
                    null,
                    false
                )
        );
    }
    protected _dataServiceDataChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dataServiceIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dataServiceOperationChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _defaultSortListPropertyChanged(change: PropertyChange<string>)
    {
        if (this._isSavingRows())
            return;

        this._queueSearchAsync(0);
    }
    protected _defaultSortListPropertyDirectionChanged(change: PropertyChange<string>)
    {
        if (this._isSavingRows())
            return;

        this._queueSearchAsync(0);
    }
    protected _editModeChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-edit-mode");
        else
            this.removeCssClass("rad-state-edit-mode");

        this._updateViewAndEditControls();
    }
    protected _entityIconDataSourcePathChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _filterCommandsForEntityCommandMenu(entityCommands: EntityCommand[])
    {
        return entityCommands.filter(item => !item.supportsMultipleRecords.get());
    }
    protected _filterCommandsForEntitySetCommandMenu(entitySetCommands: EntityCommand[])
    {
        return entitySetCommands;
    }
    protected _foreignApplicationEntityAliasChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    public _getEntityCommandInformation(): EntityCommandInformation
    {
        const
            page = this.page.get(),
            pageDataSource = page ? page.dataSource.get() : null,
            pageData = pageDataSource ? pageDataSource.data.get() : null;

        return new EntityCommandInformation({
            caller: this,
            entityName: this.applicationEntityName.get(),
            entityNameFriendly: this.applicationEntityNameFriendly.get(),
            entitySetName: this.applicationEntityNamePlural.get(),
            entitySetNameFriendly: this.applicationEntityNamePluralFriendly.get(),
            entitySupportsSoftDelete: this.applicationEntitySupportsSoftDelete.get(),
            foreignEntityAlias: this.foreignApplicationEntityAlias.get(),
            parentEntityId: pageData ? pageData.id : null,
            parentEntityName: page instanceof DetailsPage ? page.entityName.get() : null,
            parentEntitySetName: page instanceof DetailsPage ? page.entitySetName.get() : null,
            parentEntityUrl: page ? page.url.get() : null,
            openRecordInDialog: this.openRecordInDialog.get()
        });
    }
    protected _getRowEditorControls(row: TableRowEntitySearch, cell: TableCellEntitySearch)
    {
        const
            $$tableCells = cell ? cell.$$element.get() : row.$$element.get()?.find(">td"),
            editorControls: InputControl[] = [];

        $$tableCells?.each((index, element) =>
        {
            const
                control = JNativeStatic.radControl(element);

            if (control?.editorControl instanceof InputControl)
                editorControls.push(control.editorControl);
        });

        return editorControls;
    }
    protected _getRowRecordNumber(row: TableRowEntitySearch)
    {
        const
            $$row = row.$$element.get(),
            pageSize = this.pageSize.get();

        Assert.isNotNullOrUndefined($$row, "$$row");
        Assert.isNotNullOrUndefined(pageSize, "pageSize");

        return this.currentPage * pageSize + $$row.parent().children().index($$row);
    }
    public _hasEntityCommandsForEntityCommandInformation(entityCommandInformation: Nullable<EntityCommandInformation>): boolean
    {
        if (this.entityCommandMenu)
        {
            this.entityCommandMenu.controlsGet()
                .forEach(item =>
                {
                    const
                        visibleEvaluator = item.visibleEvaluator.get();

                    if (visibleEvaluator)
                        visibleEvaluator.state.set(entityCommandInformation);
                });
        }

        return !!(this.entityCommandMenu?.visibleMenuItemCount.get());
    }
    protected _hideSaveIndicatorChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _includeSoftDeletedChanged(change: PropertyChange<boolean>)
    {
        if (this._isSavingRows())
            return;

        this._queueSearchAsync(0);
    }
    protected _isSavingRows()
    {
        const
            $$tableSearchResults = this.tableSearchResults?.$$element.get();

        Assert.isNotNullOrUndefined($$tableSearchResults, "$$tableSearchResults");

        return $$tableSearchResults.find(".rad-state-saving").length > 0;
    }
    protected _lightweightTableChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-feature-lightweight-table");
        else
            this.removeCssClass("rad-feature-lightweight-table");
    }
    protected _listPropertiesChanged(change: PropertyChange<any[]>)
    {
        const
            tableColumnClass = this.tableColumnClass.get(),
            sortable = this.sortable.get();

        Assert.isNotNullOrUndefined(this.tableSearchResults, "self.tableSearchResults");

        this.tableSearchResults.columnsClear();

        if (change.currentValue)
        {
            let
                hasEditorControls = false;

            change.currentValue.forEach((item, index) =>
            {
                if (item.editorControl)
                    hasEditorControls = true;

                const
                    tableColumnClassToUse = item.tableColumnClass || tableColumnClass;

                Assert.isNotNullOrUndefined(this.tableSearchResults, "self.tableSearchResults");

                this.tableSearchResults.addColumn(new tableColumnClassToUse({
                    dataSourceProperty: item.path,
                    text: item.name,
                    formatString: item.formatString,
                    applicationEntitySupportsSoftDelete: item.applicationEntitySupportsSoftDelete,
                    editorControl: item.editorControl,
                    editorControlProperties: item.editorControlProperties,
                    hideTitle: item.hideTitle,
                    manual: item.manual,
                    sortable: sortable && (item.sortable === undefined || item.sortable),
                    sortProperty: item.sortProperty,
                    viewControlClass: item.viewControlClass,
                    viewControlProperties: item.viewControlProperties,
                    width: item.width
                }));

                if (index === 0 && !this.defaultSortListProperty.get())
                {
                    this.defaultSortListProperty.set(item.path);
                    this.defaultSortListPropertyDirection.set(EntitySearchSortDirectionEnum.ascending);
                }
            });

            if (hasEditorControls && !this.hideSaveIndicator.get())
                this.tableSearchResults.addColumn(new TableColumnEntitySaveIndicator());

            const
                tableColumnEntityActionClass = this.tableColumnEntityActionClass.get();

            if (this.showEntityCommands.get() && tableColumnEntityActionClass)
                this.tableSearchResults.addColumn(new tableColumnEntityActionClass());
        }

        this._updateQuickSearch();
    }
    protected _messageWhenNoResultsChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _openCommandMenuAsync(commandMenu: Menu, entityCommandInformation: EntityCommandInformation): Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            if (!commandMenu || !entityCommandInformation)
                return resolve();

            commandMenu
                .openAsync()
                .then(menuItem =>
                {
                    if (!menuItem)
                    {
                        resolve(undefined);
                        return;
                    }

                    return menuItem.tag.get().entityCommand.executeAsync(entityCommandInformation, menuItem.toggleState.get());
                })
                .then(() =>
                {
                    this.searchAsync();
                    resolve();
                })
                .catch(error =>
                {
                    if (error instanceof AjaxError && error.type.get() === "ValidationApiException")
                        ErrorDialog.displayError(ArrayHelper.firstOrDefault(error.validationErrors.get() ?? [])?.errorMessage ?? "Unknown error");

                    reject(error);
                });
        });
    }
    protected _openRecordInDialogChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _pageFirstAsync()
    {
        return this.searchAsync(0);
    }
    protected _pageLastAsync()
    {
        if (this.totalRecords === null)
            return Promise.resolve();

        const
            pageSize = this.pageSize.get();

        Assert.isNotNullOrUndefined(pageSize, "pageSize");

        return this.searchAsync(Math.ceil(this.totalRecords / pageSize) - 1);
    }
    protected _pageNextAsync()
    {
        const
            pageSize = this.pageSize.get();

        Assert.isNotNullOrUndefined(pageSize, "pageSize");
        Assert.isNotNullOrUndefined(this.totalRecords, "this.totalRecords ");

        return this.searchAsync(Math.min(Math.ceil(this.totalRecords / pageSize) - 1, this.currentPage + 1));
    }
    protected _pagePreviousAsync()
    {
        return this.searchAsync(Math.max(0, this.currentPage - 1));
    }
    protected _pageSizeChanged(change: PropertyChange<number>)
    {
        this._updatePagination();
    }
    protected _postSaveRowAsync(record: any)
    {
        return Promise.resolve();
    }
    protected _postSearchAsync(page?: number, filter?: Nullable<Expression>, results?: any[], count?: number, data?: any)
    {
        return Promise.resolve();
    }
    protected _primaryApplicationEntityNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _primaryApplicationEntityNameFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _primaryApplicationEntityNamePluralChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _primaryApplicationEntityNamePluralFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _queueSearchAsync(page?: number, filter?: any, force?: boolean)
    {
        if (this._queueSearchAsyncTimeoutId)
            clearTimeout(this._queueSearchAsyncTimeoutId);

        this._queueSearchAsyncTimeoutId = window.setTimeout(() =>
        {
            this._queueSearchAsyncTimeoutId = 0;
            this.searchAsync(page, filter, force);
        }, 100);
    }
    protected _requiredPropertiesChanged(change: PropertyChange<any[]>)
    {
        /* Intentionally empty */
    }
    protected _rowDisable(row: TableRowEntitySearch)
    {
        row.enabled.set(false);
        row.controlsGet().forEach(item => { if ((item as any).editorControl) (item as any).editorControl.enabled.set(false); });
    }
    protected _rowEnable(row: TableRowEntitySearch)
    {
        row.enabled.set(true);
        row.controlsGet().forEach(item => { if ((item as any).editorControl) (item as any).editorControl.enabled.set(true); });
    }
    protected _rowSelectTypeChanged(change: PropertyChange<TableRowSelectTypeEnum>)
    {
        if (!this.tableSearchResults)
            return;

        this.tableSearchResults.rowSelectType.set(change.currentValue);
    }
    protected _saveManualCellChangeAsync(listProperty: string, change: { path: string, tableRow: TableRowEntitySearch })
    {
        return Promise.resolve();
    }
    protected _saveRowAsync(row: TableRowEntitySearch)
    {
        const
            rowDataSource = row.dataSource.get();

        if (!rowDataSource)
            return Promise.reject();

        const
            dataOriginal = rowDataSource.dataOriginal.get(),
            data = rowDataSource.data.get(),
            manualListProperties = this.listProperties.get()?.filter(item => item.manual) ?? [],
            dataOriginalWithoutManualProperties = {},
            dataWithoutManualProperties = {};

        ObjectHelper.copySimpleProperties(dataOriginal, dataOriginalWithoutManualProperties, property =>
        {
            return !manualListProperties.some(item => item.path === property);
        });

        ObjectHelper.copySimpleProperties(data, dataWithoutManualProperties, property =>
        {
            return !manualListProperties.some(item => item.path === property);
        });

        const
            validationData = {
                validationErrors: []
            };

        rowDataSource.triggerHandler("validating", validationData);

        if (validationData.validationErrors.length)
            return Promise.reject(validationData);

        const
            beforeSaveChangesData = {
                promises: []
            };

        rowDataSource.triggerHandler("beforeSaveChanges", beforeSaveChangesData);

        let
            savedRecord: any;

        return (
            Promise.all(beforeSaveChangesData.promises)
                .then(() =>
                {
                    const
                        applicationEntityNamePlural = this.applicationEntityNamePlural.get();

                    Assert.isNotNullOrUndefined(applicationEntityNamePlural, "applicationEntityNamePlural");

                    return (
                        ServiceManager.getRequired(WebApiClientService)
                            .createEntitySaveRequest(
                                applicationEntityNamePlural,
                                dataWithoutManualProperties,
                                dataOriginalWithoutManualProperties,
                                row.enabled.get()
                            )
                            .executeAsync()
                    );
                })
                .then(data =>
                {
                    savedRecord = data;

                    const
                        afterSaveChangesData = {
                            promises: []
                        };

                    rowDataSource.triggerHandler("afterSaveChanges", afterSaveChangesData);

                    return (
                        Promise.all(afterSaveChangesData.promises)
                            .then(() =>
                            {
                                return data;
                            })
                    );
                })
                .then(data =>
                {
                    if (this.dataServiceId.get())
                        return Promise.resolve([savedRecord]);
                    else
                    {
                        return (
                            this
                                ._createSearchWebRequest(
                                    0,
                                    new ComparisonExpression({
                                        path: "id",
                                        value: data.id
                                    })
                                )
                                .executeAsync()
                        );
                    }
                })
                .then(data =>
                {
                    if (data?.length)
                    {
                        return (
                            this._postSaveRowAsync(data[0])
                                .then(() =>
                                {
                                    for (let property in data[0])
                                        rowDataSource.propertyValueByPathSet(property, data[0][property]);

                                    rowDataSource.acceptChanges();
                                })
                        );
                    }
                })
        );
    }
    protected _searchAsync(page?: number, filter?: Expression)
    {
        if (this.designMode.get())
            return Promise.resolve();

        this._checkPermissions();

        if (page === undefined)
            page = this.currentPage;

        let
            combinedFilter = null;

        if (filter === undefined)
            filter = this.currentFilter;

        combinedFilter = this._combineFilters(filter);

        const
            webRequest = this._createSearchWebRequest(page, combinedFilter);

        this._loadingEnqueue();

        let
            searchResults: any[],
            searchResultsCount: number;

        return (
            webRequest
                .executeAsync()
                .then(data =>
                {
                    searchResults = data;
                    searchResultsCount = data.total;
                    this.searchResultsCount.set(searchResultsCount);
                    this.searchResultsResponse.set(data);

                    return this._postSearchAsync(page, filter, searchResults, searchResultsCount);
                })
                .then(() =>
                {
                    this.loaded = true;
                    this.currentPage = page!;
                    this.totalRecords = searchResultsCount;
                    this.currentFilter = filter;

                    if (this.tableSearchResults)
                    {
                        this.tableSearchResults.dataSource.set(new DataSource({ data: searchResults }));
                        this._updatePagination();
                        this._updateTableHeader();
                        this._updateViewAndEditControls();

                        this._triggerSearchResultsUpdated();
                    }

                    this.lastSearch.set(DateTimeInfo.utcNow().toString("yyyy-MM-ddTHH:mm:ss.fffffff"));
                })
                .catch(error =>
                {
                    if (this.tableSearchResults)
                        this.tableSearchResults.dataSource.set(null);

                    throw error;
                })
                .finally(() =>
                {
                    this._loadingDequeue();
                })
        );
    }
    protected _searchResultsCountChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }
    protected _searchResultsResponseChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _searchUsingDataServiceAsync(page?: number)
    {
        if (this.designMode.get())
            return Promise.resolve();

        if (page === undefined)
            page = this.currentPage;

        const
            webRequest = this._createSearchWebRequestDataService(page);

        this._loadingEnqueue();

        let
            searchResults: any[],
            searchResultsCount: number;

        return (
            webRequest
                .executeAsync()
                .then(data =>
                {
                    this.searchResultsResponse.set(data);
                    searchResults = data.results[0];
                    searchResultsCount = data.count;
                    this.searchResultsCount.set(searchResultsCount);

                    return this._postSearchAsync(page, null, searchResults, searchResultsCount, data);
                })
                .then(() =>
                {
                    this.loaded = true;
                    this.currentPage = page!;
                    this.totalRecords = searchResultsCount;
                    this.currentFilter = null;

                    if (this.tableSearchResults)
                    {
                        this.tableSearchResults.dataSource.set(new DataSource({ data: searchResults }));
                        this._updatePagination();
                        this._updateTableHeader();
                        this._updateViewAndEditControls();

                        this._triggerSearchResultsUpdated();
                    }

                    this.lastSearch.set(DateTimeInfo.utcNow().toString("yyyy-MM-ddTHH:mm:ss.fffffff"));
                })
                .catch(error =>
                {
                    if (this.tableSearchResults)
                        this.tableSearchResults.dataSource.set(null);

                    throw error;
                })
                .finally(() =>
                {
                    this._loadingDequeue();
                })
        );
    }
    public _setSortListProperty(propertyPath?: string): void
    {
        if (!propertyPath)
            return;

        if (this.sortByOrderProperty.get())
            return;

        const
            defaultSortListProperty = this.defaultSortListProperty.get(),
            defaultSortListPropertyDirection = this.defaultSortListPropertyDirection.get();

        if (defaultSortListProperty !== propertyPath)
        {
            this.defaultSortListProperty.set(propertyPath);
            this.defaultSortListPropertyDirection.set(EntitySearchSortDirectionEnum.ascending);
        }
        else
        {
            this.defaultSortListPropertyDirection.set(
                defaultSortListPropertyDirection === EntitySearchSortDirectionEnum.ascending
                    ? EntitySearchSortDirectionEnum.descending
                    : EntitySearchSortDirectionEnum.ascending
            );
        }
    }
    protected _setTableRowIcons()
    {
        const
            showEntityIcons = this.showEntityIcons.get(),
            applicationEntityNamePlural = this.applicationEntityNamePlural.get();

        Assert.isNotNullOrUndefined(this.tableSearchResults, "this.tableSearchResults");

        this.tableSearchResults.rowIcon.set(showEntityIcons && applicationEntityNamePlural ? StringHelper.format("rad-icon-{0}-16", applicationEntityNamePlural.toLowerCase()) : null);
        this.tableSearchResults.rowIconDataSourcePath.set(this.entityIconDataSourcePath.get());
        this.tableSearchResults.rowIconMobile.set(showEntityIcons && applicationEntityNamePlural ? StringHelper.format("rad-icon-{0}-48", applicationEntityNamePlural.toLowerCase()) : null);
    }
    protected _setupEntityCommands()
    {
        this.entityCommands = this._createEntityCommands();

        if (!this.showEntityCommands.get())
            return;

        const
            entityName = this.applicationEntityName.get();

        Assert.isNotNullOrUndefined(entityName, "entityName");

        if (this.entityCommandMenu)
            this.entityCommandMenu.off("visibleMenuItemCountChanged", { self: this }, this.onEntityCommandMenuVisibleMenuItemCountChanged);

        this.entityCommandMenu = radWindow.entityManager?.createCommandMenu(entityName, this._filterCommandsForEntityCommandMenu(this.entityCommands ?? []));

        if (this.entityCommandMenu)
            this.entityCommandMenu.on("visibleMenuItemCountChanged", { self: this }, this.onEntityCommandMenuVisibleMenuItemCountChanged);
    }
    protected _setupEntitySetCommands()
    {
        this.entitySetCommands = this._createEntitySetCommands();

        const
            entityName = this.applicationEntityName.get();

        Assert.isNotNullOrUndefined(entityName, "entityName");

        if (this.entitySetCommandMenu)
            this.entitySetCommandMenu.off("visibleMenuItemCountChanged", { self: this }, this.onEntitySetCommandMenuVisibleMenuItemCountChanged);

        this.entitySetCommandMenu = radWindow.entityManager?.createCommandMenu(entityName, this._filterCommandsForEntitySetCommandMenu(this.entitySetCommands ?? [])) ?? undefined;

        if (this.entitySetCommandMenu)
            this.entitySetCommandMenu.on("visibleMenuItemCountChanged", { self: this }, this.onEntitySetCommandMenuVisibleMenuItemCountChanged);

        this._updateEntitySetCommandsState();
    }
    protected _setupListPropertiesAsync()
    {
        return Promise.resolve();
    }
    protected _shouldSearch()
    {
        const
            page = this.page.get(),
            pageInstanceOfDetailsPage = page instanceof DetailsPage,
            pageDataSource = page ? page.dataSource.get() : null;

        if (!page)
            return false;

        return (
            page.activated.get()
            && this._isShown()
            && (
                !pageInstanceOfDetailsPage
                || pageDataSource
            )
        );
    }
    protected _showEntityCommandsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _showEntityIconsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _showEntitySetCommandsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _showQuickSearchChanged(change: PropertyChange<boolean>)
    {
        this._updateQuickSearch();
    }
    protected _sortableChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _sortByOrderPropertyChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-feature-sorted-by-order");
        else
            this.removeCssClass("rad-feature-sorted-by-order");

        this._updateQuickSearch();
    }
    protected _tableColumnClassChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _tableColumnEntityActionClassChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _tableRowClassChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _triggerSearchResultsUpdated()
    {
        this.triggerHandler("searchResultsUpdated");
    }
    protected _updateEntitySetCommandsState()
    {
        if (this.entitySetCommandMenu?.visibleMenuItemCount.get())
            this.removeCssClass("rad-state-no-entity-set-commands");
        else
            this.addCssClass("rad-state-no-entity-set-commands");

        // bodge in extra entity set command controls for UI theming.

        if (!this.panelEntitySetControls)
        {
            this.panelEntitySetControls =
                new Panel({ cssClass: "rad-control-panel-entity-set-controls" });

            this.panelEntitySetControls.prependTo(this.$$element.get());

            this.textBoxSearch?.$$element.get()?.appendTo(this.panelEntitySetControls.$$element.get());

            this.panelSpacer =
                new Panel({ cssClass: "rad-spacer" })
                    .appendTo(this.panelEntitySetControls);
        }

        // TODO: base this on some magic for determining which actions want to be pulled out of the menu
        /*if (!this.buttonEntitySetControlNew)
            this.buttonEntitySetControlNew = new Button({ enabled: true, inline: true, text: "New" })
                .appendTo(this.panelEntitySetControls);

        if (!this.panelSpacerMobile)
            this.panelSpacerMobile = new Panel({ cssClass: "rad-spacer rad-device-visible-when-narrow" })
                .appendTo(this.panelEntitySetControls);

        if (!this.comboBoxEntitySetControlSort)
            this.comboBoxEntitySetControlSort = new ComboBox({ enabled: true, inline: true, placeholder: "Sort", listDataSource: [{ value: "name", description: "Name" }, { value: "type", description: "Type" }], listDataSourceValueProperty: "value" })
                .appendTo(this.panelEntitySetControls);*/

        if (!this.buttonRefresh)
        {
            this.buttonRefresh = new Button({ enabled: true, inline: true, icon: "refresh", showLabel: false, cssClass: "compact rad-refresh" })
                .appendTo(this.panelEntitySetControls);

            this.buttonRefresh.on("click", { self: this }, this.onButtonRefreshClicked);
        }

        if (!this.buttonEntitySetControlOpenActionMenu)
        {
            this.buttonEntitySetControlOpenActionMenu = new Button({ enabled: true, inline: true, icon: "ellipsis", showLabel: false, cssClass: "compact rad-action-entity-set-actions-open" })
                .appendTo(this.panelEntitySetControls);

            this.buttonEntitySetControlOpenActionMenu.on("click", { self: this }, this.onButtonEntitySetControlOpenActionMenuClicked);
        }

        this.buttonEntitySetControlOpenActionMenu.visible.set(this.showEntitySetCommands.get());

        this.textBoxSearch?.placeholder.set(StringHelper.format("Search {0}", this.applicationEntityNamePluralFriendly.get()));
    }
    protected _updatePagination()
    {
        if (!this.tableSearchResults)
            return;

        const
            totalRecords = this.totalRecords ?? 0,
            pageSize = this.pageSize.get();

        Assert.isNotNullOrUndefined(totalRecords, "totalRecords");
        Assert.isNotNullOrUndefined(pageSize, "pageSize");

        const
            isSavingRows = this._isSavingRows(),
            lastPage = Math.ceil(totalRecords / pageSize) - 1,
            onFirstPage = this.currentPage <= 0,
            onLastPage = this.currentPage >= Math.ceil(totalRecords / pageSize) - 1;

        this.tableSearchResults.footerVisible.set(!!totalRecords);

        Assert.isNotNullOrUndefined(this.buttonFirst, "this.buttonFirst");
        Assert.isNotNullOrUndefined(this.buttonPrevious, "this.buttonPrevious");
        Assert.isNotNullOrUndefined(this.buttonNext, "this.buttonNext");
        Assert.isNotNullOrUndefined(this.buttonLast, "this.buttonLast");
        Assert.isNotNullOrUndefined(this.textPaginationStatus, "this.textPaginationStatus");

        this.buttonFirst.enabled.set(!isSavingRows && !onFirstPage);
        this.buttonPrevious.enabled.set(!isSavingRows && !onFirstPage);
        this.buttonNext.enabled.set(!isSavingRows && !onLastPage);
        this.buttonLast.enabled.set(!isSavingRows && !onLastPage);

        this.textPaginationStatus.text.set(StringHelper.format("Page {0} of {1} ({2} records)", this.currentPage + 1, lastPage + 1, this.totalRecords));

        if (this.totalRecords)
            this.textPaginationStatus.show();
        else
            this.textPaginationStatus.hide();

        if (onFirstPage)
            this.addCssClass("rad-state-first-page");
        else
            this.removeCssClass("rad-state-first-page");

        if (onLastPage)
            this.addCssClass("rad-state-last-page");
        else
            this.removeCssClass("rad-state-last-page");
    }
    protected _updateQuickSearch()
    {
        if (!this.textBoxSearch)
            return;

        const
            showQuickSearch = this.showQuickSearch.get(),
            listProperties = this.listProperties.get(),
            hasStringListProperty = listProperties ? listProperties.some(item => item.isString) : null,
            sortByOrderProperty = this.sortByOrderProperty.get();

        if (showQuickSearch && hasStringListProperty && !sortByOrderProperty)
            this.textBoxSearch.show();
        else
            this.textBoxSearch.hide();
    }
    public _updateTableColumnEntityAction(): void
    {
        const
            $$element = this.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        const
            $$entityActionCells = $$element.find(".rad-control-table-cell-entity-action"),
            $$entityActionCellsWithoutCommands = $$entityActionCells.filter(".rad-state-no-entity-commands");

        if ($$entityActionCells.length && $$entityActionCells.length !== $$entityActionCellsWithoutCommands.length)
            this.removeCssClass("rad-state-no-entity-commands");
        else
            this.addCssClass("rad-state-no-entity-commands");
    }
    protected _updateTableHeader()
    {
        Assert.isNotNullOrUndefined(this.tableSearchResults, "this.tableSearchResults");

        this.tableSearchResults.columnsGet().forEach(item =>
        {
            if ((item as any)._updateDisplay)
                (item as any)._updateDisplay();
        });
    }
    protected _updateViewAndEditControls()
    {
        const
            $$element = this.$$element.get(),
            editMode = this.editMode.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        $$element.find(".edit-control")
            .each((index, element) =>
            {
                const
                    control = JNativeStatic.radControl(element);

                if (control)
                    control.visible.set(editMode);
            });

        $$element.find(".view-control")
            .each((index, element) =>
            {
                const
                    control = JNativeStatic.radControl(element);

                if (control)
                    control.visible.set(!editMode);
            });
    }
    protected _validationErrorsHide(row: TableRowEntitySearch, cell: Nullable<TableCellEntitySearch>)
    {
        /* Intentionally empty */
    }
    protected _validationErrorsShow(row: TableRowEntitySearch, cell: Nullable<TableCellEntitySearch>, validationErrors: string[])
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: EntitySearchPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.applicationEntityName = new PropertyString(this, propertyValues.applicationEntityName, this._applicationEntityNameChanged);
        this.applicationEntityNameFriendly = new PropertyString(this, propertyValues.applicationEntityNameFriendly, this._applicationEntityNameFriendlyChanged);
        this.applicationEntityNamePlural = new PropertyString(this, propertyValues.applicationEntityNamePlural, this._applicationEntityNamePluralChanged);
        this.applicationEntityNamePluralFriendly = new PropertyString(this, propertyValues.applicationEntityNamePluralFriendly, this._applicationEntityNamePluralFriendlyChanged);
        this.applicationEntitySupportsRowVersion = new PropertyBoolean(this, propertyValues.applicationEntitySupportsRowVersion, this._applicationEntitySupportsRowVersionChanged);
        this.applicationEntitySupportsSoftDelete = new PropertyBoolean(this, propertyValues.applicationEntitySupportsSoftDelete, this._applicationEntitySupportsSoftDeleteChanged);
        this.baseFilter = new PropertyObject(this, propertyValues.baseFilter, this._baseFilterChanged);
        this.dataServiceId = new PropertyString(this, propertyValues.dataServiceId, this._dataServiceIdChanged);
        this.dataServiceData = new PropertyString(this, propertyValues.dataServiceData, this._dataServiceDataChanged);
        this.dataServiceOperation = new PropertyString(this, propertyValues.dataServiceOperation, this._dataServiceOperationChanged);
        this.defaultSortListProperty = new PropertyString(this, propertyValues.defaultSortListProperty, this._defaultSortListPropertyChanged);
        this.defaultSortListPropertyDirection = new PropertyString(this, propertyValues.defaultSortListPropertyDirection, this._defaultSortListPropertyDirectionChanged);
        this.editMode = new PropertyBoolean(this, propertyValues.editMode, this._editModeChanged);
        this.entityIconDataSourcePath = new PropertyString(this, propertyValues.entityIconDataSourcePath, this._entityIconDataSourcePathChanged);
        this.foreignApplicationEntityAlias = new PropertyString(this, propertyValues.foreignApplicationEntityAlias, this._foreignApplicationEntityAliasChanged);
        this.includeSoftDeleted = new PropertyBoolean(this, propertyValues.includeSoftDeleted, this._includeSoftDeletedChanged);
        this.hideSaveIndicator = new PropertyBoolean(this, propertyValues.hideSaveIndicator, this._hideSaveIndicatorChanged);
        this.lastSearch = new PropertyString(this);
        this.lightweightTable = new PropertyBoolean(this, propertyValues.lightweightTable, this._lightweightTableChanged);
        this.listProperties = new PropertyObject(this, propertyValues.listProperties, this._listPropertiesChanged);
        this.messageWhenNoResults = new PropertyString(this, propertyValues.messageWhenNoResults, this._messageWhenNoResultsChanged);
        this.openRecordInDialog = new PropertyBoolean(this, propertyValues.openRecordInDialog, this._openRecordInDialogChanged);
        this.pageSize = new PropertyInt32(this, propertyValues.pageSize, this._pageSizeChanged);
        this.primaryApplicationEntityName = new PropertyString(this, propertyValues.primaryApplicationEntityName, this._primaryApplicationEntityNameChanged);
        this.primaryApplicationEntityNameFriendly = new PropertyString(this, propertyValues.primaryApplicationEntityNameFriendly, this._primaryApplicationEntityNameFriendlyChanged);
        this.primaryApplicationEntityNamePlural = new PropertyString(this, propertyValues.primaryApplicationEntityNamePlural, this._primaryApplicationEntityNamePluralChanged);
        this.primaryApplicationEntityNamePluralFriendly = new PropertyString(this, propertyValues.primaryApplicationEntityNamePluralFriendly, this._primaryApplicationEntityNamePluralFriendlyChanged);
        this.requiredProperties = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.requiredProperties, this._requiredPropertiesChanged);
        this.rowSelectType = new Property<TableRowSelectTypeEnum>(this, "System.String, System.Private.CoreLib", propertyValues.rowSelectType, this._rowSelectTypeChanged);
        this.searchResultsCount = new PropertyInt32(this, propertyValues.searchResultsCount, this._searchResultsCountChanged);
        this.searchResultsResponse = new PropertyObject(this, propertyValues.searchResultsResponse, this._searchResultsResponseChanged);
        this.sortable = new PropertyBoolean(this, propertyValues.sortable, this._sortableChanged);
        this.showEntityCommands = new PropertyBoolean(this, propertyValues.showEntityCommands, this._showEntityCommandsChanged);
        this.showEntityIcons = new PropertyBoolean(this, propertyValues.showEntityIcons, this._showEntityIconsChanged);
        this.showEntitySetCommands = new PropertyBoolean(this, propertyValues.showEntitySetCommands, this._showEntitySetCommandsChanged);
        this.showQuickSearch = new PropertyBoolean(this, propertyValues.showQuickSearch, this._showQuickSearchChanged);
        this.sortByOrderProperty = new PropertyBoolean(this, propertyValues.sortByOrderProperty, this._sortByOrderPropertyChanged);
        this.tableColumnClass = new PropertyObject(this, propertyValues.tableColumnClass, this._tableColumnClassChanged);
        this.tableColumnEntityActionClass = new PropertyObject(this, propertyValues.tableColumnEntityActionClass, this._tableColumnEntityActionClassChanged);
        this.tableRowClass = new PropertyObject(this, propertyValues.tableRowClass, this._tableRowClassChanged);

        this.includeSoftDeleted.overrideGet(next =>
        {
            const
                page = this.page.get();

            if (page instanceof DetailsPage && page.isSoftDeleted.get())
                return true;

            return next();
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.textBoxSearch, "this.textBoxSearch");
        Assert.isNotNullOrUndefined(this.tableSearchResults, "this.tableSearchResults");
        Assert.isNotNullOrUndefined(this.buttonFirst, "this.buttonFirst");
        Assert.isNotNullOrUndefined(this.buttonPrevious, "this.buttonPrevious");
        Assert.isNotNullOrUndefined(this.buttonNext, "this.buttonNext");
        Assert.isNotNullOrUndefined(this.buttonLast, "this.buttonLast");

        this.textBoxSearch.on("valueChanged", { self: this }, this.onTextBoxSearchValueChanged);
        this.textBoxSearch.on("keydown", { self: this }, this.onTextBoxSearchKeyDown);
        this.tableSearchResults.on("rowClicked", { self: this }, this.onRowClicked);
        this.tableSearchResults.on("rowDataSourceChanged", { self: this }, this.onRowDataSourceChanged);

        this.buttonFirst.on("click", { self: this }, this.onButtonFirstClicked);
        this.buttonPrevious.on("click", { self: this }, this.onButtonPreviousClicked);
        this.buttonNext.on("click", { self: this }, this.onButtonNextClicked);
        this.buttonLast.on("click", { self: this }, this.onButtonLastClicked);

        $$(document).on("SeriouslyRAD.PermissionCache:PermissionsChanged", { self: this }, this.onPermissionsChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        // Design Mode State
        this.designModePanel =
            new Panel({ cssClass: "rad-overlay-design-mode" });

        this.designModePanel._containerElementRef.current.append(StringHelper.formatWithHtmlEncode("<span>Entity Search: {0}</span>", this.applicationEntityNamePluralFriendly.get()));

        Assert.isNotNullOrUndefined($$element, "$$element");

        this.textBoxSearch = new TextBox({ icon: "search" });

        Assert.isNotNullOrUndefined(this.textBoxSearch, "this.textBoxSearch");

        this.tableSearchResults = new Table({
            lightweight: this.lightweightTable.get(),
            linkUrls: true,
            messageWhenNoRows: this.messageWhenNoResults.get(),
            rowClickable: true,
            rowSelectType: this.rowSelectType.get(),
            tableRowClass: this.tableRowClass.get(),
        });

        Assert.isNotNullOrUndefined(this.tableSearchResults, "this.tableSearchResults");

        this.paginationContainer = new Panel({ cssClass: "rad-control-pagination" })
            .appendTo(this.tableSearchResults.tableFoot);

        this.buttonFirst = new Button({ enabled: false, inline: true, text: "First" })
            .appendTo(this.paginationContainer);

        this.buttonPrevious = new Button({ enabled: false, inline: true, text: "Prev" })
            .appendTo(this.paginationContainer);

        this.textPaginationStatus = new Text()
            .appendTo(this.paginationContainer);

        this.buttonNext = new Button({ enabled: false, inline: true, text: "Next" })
            .appendTo(this.paginationContainer);

        this.buttonLast = new Button({ enabled: false, inline: true, text: "Last" })
            .appendTo(this.paginationContainer);

        // Add to control
        this.textBoxSearch.appendTo($$element);
        this.tableSearchResults.appendTo($$element);
        this.designModePanel.$$element.get()?.appendTo($$element);
        this._updatePagination();

        return $$element;
    }
    protected override _dispose()
    {
        // #region Bound Document Events

        $$(document).off("SeriouslyRAD.PermissionCache:PermissionsChanged", { self: this }, this.onPermissionsChanged);

        // #endregion

        super._dispose();
    }

    public override remove()
    {
        // #region Created Elements

        this.removeControls([
            this.designModePanel,
            this.textBoxSearch,
            this.tableSearchResults,
            this.buttonFirst,
            this.buttonPrevious,
            this.buttonNext,
            this.buttonLast,
            this.textPaginationStatus,
            this.entityCommandMenu,
            this.entitySetCommandMenu,
            this.panelEntitySetControls,
            this.panelSpacer,
            this.buttonEntitySetControlOpenActionMenu
        ]);

        // #endregion

        super.remove();
    }

    protected override onAncestorVisibilityChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (change.currentValue)
            self._queueSearchAsync();
    }

    // #endregion

    // #region Event Handlers

    protected onButtonEntitySetControlOpenActionMenuClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this,
            entityCommandInformation = self._getEntityCommandInformation();

        if (!entityCommandInformation)
            return;

        self.openEntitySetCommandMenuAsync(entityCommandInformation);
    }
    protected onButtonFirstClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this;

        self._pageFirstAsync()
            .then(() =>
            {
                self.scrollToAsync();
            });
    }
    protected onButtonLastClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this;

        self._pageLastAsync()
            .then(() =>
            {
                self.scrollToAsync();
            });
    }
    protected onButtonNextClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this;

        self._pageNextAsync()
            .then(() =>
            {
                self.scrollToAsync();
            });
    }
    protected onButtonPreviousClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this;

        self._pagePreviousAsync()
            .then(() =>
            {
                self.scrollToAsync();
            });
    }
    protected onButtonRefreshClicked(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.searchAsync();
    }
    protected onEntityCommandMenuVisibleMenuItemCountChanged(evt: IJNativeEvent, change: any)
    {
        const
            self = evt.data.self as this;

        self._updateEntitySetCommandsState();
    }
    protected onEntitySetCommandMenuVisibleMenuItemCountChanged(evt: IJNativeEvent, change: any)
    {
        const
            self = evt.data.self as this;

        self._updateEntitySetCommandsState();
    }
    protected override onPageActivated(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        self._queueSearchAsync();
    }
    protected onPermissionsChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._checkPermissions();
    }
    protected onRowClicked(evt: IJNativeEvent, row: TableRowEntitySearch)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this;

        if (self._isSavingRows())
            return;

        const
            rowDataSource = row ? row.dataSource.get() : null,
            record = rowDataSource ? rowDataSource.data.get() : null;

        if (!record)
            return null;

        const
            entityCommand =
                LinqArray.create(self.entityCommands)
                    .where(item => !!item.isDefault.get())
                    .orderBy(item => !ObjectHelper.isNullOrUndefined(item.entityName.get()) ? "0" : "1")
                    .firstOrDefault();

        if (!entityCommand)
            return;

        const
            entityCommandInformation = self._getEntityCommandInformation();

        if (!entityCommandInformation)
            return;

        entityCommandInformation.record.set(record);

        entityCommand.executeAsync(entityCommandInformation)
            .then(() =>
            {
                self.searchAsync();
            });
    }
    protected onRowDataSourceChanged(evt: IJNativeEvent, change: any)
    {
        const
            self = evt.data.self as this;

        if (self.__settingUpListProperties)
            return;

        const
            listProperties = self.listProperties.get(),
            listProperty = listProperties ? ArrayHelper.firstOrDefault(listProperties, item => item.path === change.path) : null;

        if (listProperty?.manual)
        {
            self.saveManualCellChangeAsync(listProperty, change);
            return;
        }

        self.saveRowAsync(change.tableRow);
    }
    protected onTextBoxSearchKeyDown(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        if (evt.originalEvent.key === "Enter")
        {
            // If search queued, cancel and search now
            if (self._queueSearchAsyncTimeoutId)
            {
                window.clearTimeout(self._queueSearchAsyncTimeoutId);
                self._queueSearchAsyncTimeoutId = 0;
                self.searchAsync();
            }
        }
    }
    protected onTextBoxSearchValueChanged(evt: IJNativeEvent, change: any)
    {
        const
            self = evt.data.self as this;

        if (StringHelper.isNullOrEmpty(change.currentValue) && StringHelper.isNullOrEmpty(change.originalValue))
            return;

        if (self._textBoxSearchValueChangedTimeoutId)
            clearTimeout(self._textBoxSearchValueChangedTimeoutId);

        self._textBoxSearchValueChangedTimeoutId = window.setTimeout(() =>
        {
            self._textBoxSearchValueChangedTimeoutId = 0;
            self.searchAsync(0);
        }, 500);
    }

    // #endregion

    // #endregion
}

// #endregion